import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { useAuthContext } from '../context/AuthContext';

const VerifyEmailScreen: React.FC = () => {
  const { user, checkEmailVerification, logout } = useAuthContext();
  const [checking, setChecking] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleContinue = async () => {
    setChecking(true);
    setError(null);
    try {
      await checkEmailVerification();
      // If email still not verified, show error
      setError('Email not verified yet. Please check your inbox and click the verification link.');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    setLoggingOut(false);
  };

  return (
    <View style={styles.container}>
      {/* Icon */}
      <View style={styles.iconWrap}>
        <Icon name="mail-outline" size={56} color="#C2185B" />
      </View>

      <Text style={styles.title}>Verify your email</Text>
      <Text style={styles.subtitle}>
        We sent a verification link to
      </Text>
      {user?.email ? (
        <Text style={styles.email}>{user.email}</Text>
      ) : null}
      <Text style={styles.instruction}>
        Open the email and tap the link to verify your account. Once verified, come back here and tap Continue.
      </Text>

      {/* Error */}
      {error ? (
        <View style={styles.errorBanner}>
          <Icon name="alert-circle-outline" size={16} color="#B91C1C" />
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}

      {/* Continue after verifying */}
      <TouchableOpacity
        style={styles.continueBtn}
        onPress={handleContinue}
        disabled={checking}
        activeOpacity={0.85}>
        {checking
          ? <ActivityIndicator color="#C2185B" />
          : (
            <>
              <Icon name="checkmark-circle-outline" size={18} color="#C2185B" />
              <Text style={styles.continueBtnText}>I've Verified — Continue</Text>
            </>
          )}
      </TouchableOpacity>

      {/* Spam warning */}
      <View style={styles.spamBanner}>
        <Icon name="warning-outline" size={15} color="#92400E" />
        <Text style={styles.spamText}>
          Don't see it? Please also check your <Text style={styles.spamBold}>Spam</Text> or <Text style={styles.spamBold}>Junk</Text> folder.
        </Text>
      </View>

      {/* Use a different account */}
      <TouchableOpacity style={styles.logoutBtn} onPress={handleLogout} disabled={loggingOut} activeOpacity={0.85}>
        {loggingOut
          ? <ActivityIndicator color="#fff" />
          : (
            <>
              <Icon name="swap-horizontal-outline" size={18} color="#fff" />
              <Text style={styles.logoutText}>Use a different account</Text>
            </>
          )}
      </TouchableOpacity>
    </View>
  );
};

export default VerifyEmailScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1, backgroundColor: '#FDF6F0',
    alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 32,
  },
  iconWrap: {
    width: 100, height: 100, borderRadius: 50,
    backgroundColor: '#FCE4EC',
    alignItems: 'center', justifyContent: 'center',
    marginBottom: 28,
  },
  title: {
    fontSize: 26, fontWeight: '900', color: '#2C1810',
    marginBottom: 10, textAlign: 'center',
  },
  subtitle: {
    fontSize: 15, color: '#8B6355', textAlign: 'center',
  },
  email: {
    fontSize: 15, fontWeight: '700', color: '#C2185B',
    marginTop: 4, marginBottom: 16, textAlign: 'center',
  },
  instruction: {
    fontSize: 14, color: '#8B6355', textAlign: 'center',
    lineHeight: 22, marginBottom: 24,
  },
  errorBanner: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 8,
    backgroundColor: '#FEE2E2', borderRadius: 10, padding: 12,
    marginBottom: 20, width: '100%',
  },
  errorText: { flex: 1, fontSize: 13, color: '#B91C1C', lineHeight: 18 },
  mailBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#C2185B', borderRadius: 14,
    paddingVertical: 15, paddingHorizontal: 32,
    width: '100%', justifyContent: 'center',
    marginBottom: 12,
    shadowColor: '#C2185B', shadowOpacity: 0.35,
    shadowRadius: 10, shadowOffset: { width: 0, height: 5 },
    elevation: 6,
  },
  mailBtnText: { color: '#fff', fontSize: 16, fontWeight: '800' },
  continueBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    borderWidth: 2, borderColor: '#C2185B', borderRadius: 14,
    paddingVertical: 14, paddingHorizontal: 32,
    width: '100%', justifyContent: 'center',
    marginBottom: 20,
  },
  continueBtnText: { color: '#C2185B', fontSize: 15, fontWeight: '700' },
  spamBanner: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 8,
    backgroundColor: '#FFF3CD', borderRadius: 10, padding: 12,
    marginBottom: 24, width: '100%',
  },
  spamText: { flex: 1, fontSize: 13, color: '#92400E', lineHeight: 18 },
  spamBold: { fontWeight: '800' },
  logoutBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#6B7280', borderRadius: 14,
    paddingVertical: 15, paddingHorizontal: 32,
    width: '100%', justifyContent: 'center',
  },
  logoutText: { color: '#fff', fontSize: 15, fontWeight: '700' },
});
